import { Box, Typography } from "@material-ui/core";
import { FC, memo } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import Error from "@material-ui/icons/Error";
import { ALL_ROLES } from "constants/constants";
import { useScheduleCellTheme } from "hooks/useTheme";
import { useGetByHelperOf } from "hooks/useScheduleHooks";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { daScheduleRoleKeyToId } from "mocks/roleDA";
import { SCHEDULE_DATE_COLUMN_IDS } from "pages/Schedule/scheduleTable";
import { UserData } from "types/User";
import { getTimeAfterPost } from "utils/filters";
import {
  getScheduleReportForUserDate,
  getUserScheduleHours,
  returnRoleHours,
  returnRoleName,
} from "utils/scheduleHelpers";

const CANCELLED_ROLES = [
  daScheduleRoleKeyToId.clientCancelled,
  daScheduleRoleKeyToId.mycuCancelled,
  daScheduleRoleKeyToId.rejected,
  daScheduleRoleKeyToId.absent,
];

interface PropsType {
  columnId: string;
  userData: UserData;
  classes: any;
  date?: string;
}

const HoursCell = memo(({ userId, classes }: { userId: number; classes: any }) => {
  const hours = useTypeSelectors((state) =>
    getUserScheduleHours(
      state.scheduleTable.dataList,
      userId,
      state.scheduleTable.scheduleFilters.station,
    ),
  );

  return <Typography className={classes.tableCellContent}>{hours}</Typography>;
});

const PdfDateCell = memo(
  ({ date, userData, classes }: { date?: string; userData: UserData; classes: any }) => {
    const report = useTypeSelectors((state) =>
      getScheduleReportForUserDate(state.scheduleTable.dataList, userData.id, date),
    );
    const currentStation = useTypeSelectors((state) => state.scheduleTable.scheduleFilters.station);
    const { theme } = useScheduleCellTheme(report);
    const { helperOf } = useGetByHelperOf(date, report?.helper_of);
    const isCellDisabled = date ? getTimeAfterPost(date, 0) : true;

    return (
      <Box
        className={`${report || isCellDisabled ? classes.scheduleBox : classes.emptyScheduleBox} ${
          report || isCellDisabled ? theme.backgroundClass : ""
        }`}
      >
        {report ? (
          <>
            {report.is_available ? (
              <>
                {report?.role !== null ? (
                  <Box
                    className={`${!CANCELLED_ROLES.includes(report?.role) ? classes.cellContent : ""}`}
                  >
                    {!report?.is_published && (
                      <Error className={`${classes.unpublishedIcon} ${theme.colorClass}`} />
                    )}
                    {report?.station !== Number(currentStation) && (
                      <Typography className={`${classes.scheduleHoursText} ${theme.colorClass}`}>
                        {report?.station__title}
                      </Typography>
                    )}
                    <Typography className={`${classes.roleName} ${theme.colorClass}`}>
                      {returnRoleName(report?.role)}
                    </Typography>
                    {report?.role === daScheduleRoleKeyToId.helper &&
                    report?.station === Number(currentStation) ? (
                      <Typography className={`${classes.scheduleHoursText} ${theme.colorClass}`}>
                        {helperOf ? `${helperOf?.first_name} ${helperOf?.last_name}` : ""}
                      </Typography>
                    ) : (
                      ""
                    )}
                    {!CANCELLED_ROLES.includes(report?.role) ? (
                      <Box className={classes.scheduleHours}>
                        <AccessTimeOutlinedIcon
                          className={`${classes.scheduleHoursIcon} ${theme.colorClass}`}
                        />
                        <Typography className={`${classes.scheduleHoursText} ${theme.colorClass}`}>
                          {returnRoleHours(report?.role, report?.role_hours, report?.station)}
                          {` · ${report.shift_start_time?.slice(0, 5)}`}
                        </Typography>
                      </Box>
                    ) : (
                      ""
                    )}
                  </Box>
                ) : (
                  <Typography className={`${classes.roleName} ${theme.colorClass}`}>
                    Available
                  </Typography>
                )}
              </>
            ) : (
              <Typography className={`${classes.roleName} ${theme.colorClass}`}>
                Unavailable
              </Typography>
            )}
          </>
        ) : (
          <Typography className={`${classes.roleName} ${theme.colorClass}`}>Unavailable</Typography>
        )}
      </Box>
    );
  },
);

export const ScheudlePdfRenderCell: FC<PropsType> = memo(
  ({ columnId, userData, classes, date }) => {
    const userValue: any = userData[columnId as keyof UserData];

    if (SCHEDULE_DATE_COLUMN_IDS.has(columnId)) {
      return <PdfDateCell date={date} userData={userData} classes={classes} />;
    }

    switch (columnId) {
      case "user":
        return (
          <Typography className={classes.tableCellContent}>
            {userData.first_name} {userData.last_name}
          </Typography>
        );
      case "badge":
        return <Typography className={classes.tableCellContent}>{userValue}</Typography>;
      case "role":
        return (
          <Typography className={classes.tableCellContent}>{ALL_ROLES[userValue - 1]}</Typography>
        );
      case "transporter_id":
        return (
          <Typography className={classes.tableCellContent}>{userData.transporter_id}</Typography>
        );
      case "tenured":
        return (
          <Box className={`${classes.tableCellContent} ${classes.tenuredCell}`}>
            {userValue >= 30 ? <CheckCircleIcon className={classes.tenuredIcon} /> : ""}
            <Typography style={{ fontSize: 9 }}>{userValue}</Typography>
          </Box>
        );
      case "hours":
        return <HoursCell userId={userData.id} classes={classes} />;
      case "payroll":
        return (
          <Typography className={classes.tableCellContent}>
            {userData.station_spots?.length ? userData.station_spots[0].agency.name : "---"}
          </Typography>
        );
      default:
        return <Typography className={classes.tableCellContent}>---</Typography>;
    }
  },
);
